import React from 'react'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import {
  fetchNationalStaticData,
  NationalStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { getDefaultNationalMetaData } from '@scentregroup/shared/helpers/get-default-national-metadata'
import { useGetHrefContext } from '@scentregroup/shared/context'
import { GetServerSideProps } from '../../types/next'
import { makeApolloClient } from '../../lib/apolloClient'
import { getNationalExperiencePage } from '../../lib/page-templates/fetching'
import { Template } from '../../components/page-templates/template'
import { SGPageTemplateWithAdZones } from '../../../../src/types/page-templates'

export const getServerSideProps: GetServerSideProps<
  NationalExperiencePageProps,
  { slug: string }
> = async ({ locale, params, res }) => {
  setCacheHeaders(res)
  if (!params) {
    throw new Error('Required params not provided')
  }

  const client = makeApolloClient()
  const country = localeToCountry(locale)
  if (country === 'New Zealand') {
    return {
      notFound: true,
    }
  }

  const slug = params.slug
  if (!slug) {
    return {
      notFound: true,
    }
  }

  const [nationalHeaderData, template] = await Promise.all([
    fetchNationalStaticData(client, { country }),
    getNationalExperiencePage(client, country, slug),
  ])

  if (!nationalHeaderData || !template) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      country,
      nationalHeaderData,
      template,
    },
  }
}

interface NationalExperiencePageProps {
  country: ReturnType<typeof localeToCountry>
  nationalHeaderData: NationalStaticData
  template: SGPageTemplateWithAdZones
}

const NationalExperiencePage: React.FC<NationalExperiencePageProps> = props => {
  const { country, template, nationalHeaderData } = props
  const { title, metaTitle, shortDescription: description } = template
  const href = useGetHrefContext()()
  const canonical = React.useMemo(
    () => new URL(new URL(href).pathname, href).toString(),
    [href]
  )
  const defaultMetaData = getDefaultNationalMetaData({
    country,
    type: 'experience',
    category: title,
  })

  const metaData = {
    title: metaTitle,
    description: description || defaultMetaData.description,
    canonical,
  }

  return (
    <Layout
      country={country}
      centre={undefined}
      header={{ variant: 'direct', data: nationalHeaderData }}
      metaData={{ ...metaData }}
      pageTitle={title}
    >
      <Template template={template} />
    </Layout>
  )
}

export default NationalExperiencePage
